/**
 * AnalyticsTracker for Optimizely using V1 Batch API
 * @module
 */

var _ = require('optimizely-client-fn');
var ApiEvents = require('core/api_events');
var uuid = require('optimizely-uuid');

var EVENT_ENDPOINT = __EVENT_TRACKING_HOST__ + '/v1/events';

var actionTypes = require('core/constants/action_types');
var createError = require('core/utils/custom_error').create;
var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var trackingPolicies = require('core/constants/tracking_policies');
var Visitor = require('core/visitor');
var WindowEnv = require('core/env/window');
var xhr = require('core/utils/xhr');

var di = require('core/di');
var DocumentEnv = require('core/env/document');
var EventEmitter = require('core/event_emitter');
var EventEmitterEnums = require('core/constants/event_emitter');
var Flux = require('core/flux');
var GlobalStore = di.get('stores/global');
var TrackerEventsStore = di.get('stores/tracker_optimizely');

var cmabHelper = require('plugins/feature_cmab/cmabHelper');

var TrackerError = exports.Error = createError('OptimizelyTrackerError');

var CLIENT_ACTIVATION_EVENT_NAME = 'client_activation';
var CAMPAIGN_ACTIVATED_EVENT = 'campaign_activated';
var VIEW_ACTIVATED_TYPE = 'view_activated';

var ENABLE_EET = __EXPERIMENTAL_ENABLE_EET__;
var TRACK_EVENTS_POLLING_TIMEOUT = __TRACK_EVENTS_POLLING_TIMEOUT__;
var TRACK_EVENTS_POLLING_INTERVAL = __TRACK_EVENTS_POLLING_INTERVAL__;

/**
 * @typedef {Object} MetricDefinition
 * @property {Function} validate
 *        Throws iff the given value is not a valid value of that metric.
 * @property {Function} sanitize
 *        Returns a post-processed version of the value (e.g., truncated, rounded).
 * @property {boolean} excludeFeature
 *        Ordinarily, event tags which describe metrics are present in both
 *        the Features and Metrics areas of the event payload. This is done
 *        because it is how to backwards-compatibly imbue a tag with special
 *        'metric' semantics. If excludeFeature is set, the value is included only
 *        once, as a Metric--legacy behavior for `revenue` and `quantity`.
 */

/**
 * The subset of event tags that are designated as 'metrics', with special
 * semantics and constraints on valid values.
 *
 * @type {Object.<string, MetricDefinition>}
 *        A map from metric name to definition.
 */
var METRIC_FIELDS = {
  // An integer number of cents
  'revenue': {
    validate: validateNumericMetric,
    // Note(tyler): Truncate instead of round for consistency with Classic, which truncates in the log tier:
    // https://github.com/optimizely/optimizely/blob/5e069ae732e6eaf21b93e10fad63bfe3de5fca91/src/backend/log.py#L586
    sanitize: Math.floor,
    excludeFeature: true,
  },

  // Legacy metric from Classic
  'quantity': {
    validate: validateNumericMetric,
    sanitize: Math.floor,
    excludeFeature: true,
  },

  // A numeric (possibly floating point) metric
  'value': {
    validate: validateNumericMetric,
    sanitize: _.identity,
  },
};

var delayUntilLoad = __EXPERIMENTAL_TRACK_EVENTS_V1_ON_LOADED__;
var manualSend = __EXPERIMENTAL_MANUAL_EVENT_SEND__ ;

var AUTO_SESSION_ID = 'AUTO';
var USE_AUTO_SESSION_ID = __EXPERIMENTAL_AUTO_SESSION_ID__;

var trackLayerDecision = function(decisionData: DecisionData) {
  if (!!decisionData.isLayerConcluded) {
    Logger.debug('Layer is already concluded, skipping decision tracking');
    return;
  }
  if (decisionData.timing === trackingPolicies.TrackLayerDecisionTimingFlags.postRedirectPolicy) {
    createBatchFromDecisionData(decisionData);
  } else {
    queueDecision(decisionData);
  }
};

/**
 * Getter returns a function which logs a ViewEventTicket to tracker_optimizely store
 * @return {onPageActivated}
 */
var onPageActivated = [
  /**
   * @return {onPageActivated}
   */
  function() {
    /**
     * @param {ViewEventData} viewEventData
     */
    return function(viewEventData) {
      queueEvent(createViewEvent(viewEventData), viewEventData.userFeatures, getDecisionsFromLayerStates(viewEventData.layerStates));
    };
  }
];

/**
 * Getter returns a function which logs a ClientActivationEvent and visitor data to the store
 * @return {Function}
 */
var onClientActivation = [
  /**
   * @return {onClientActivation}
   */
  function() {
    /**
     * @param {ClientActivationEventData} eventData
     */
    return function(eventData:ClientActivationEventData) {
      //One time set up
      createBatch(eventData);
      queueEvent(createClientActivationEvent(eventData), eventData.userFeatures, getDecisionsFromLayerStates(eventData.layerStates));
    };
  }
];


/**
 * Getter returns an  which logs a CustomEventTicket to the store
 * @return {onCustomEvent}
 */
var onCustomEvent = [
  /**
   * @return {onCustomEvent}
   */
  function() {
    /**
     * @param {CustomEventData} eventData
     */
    return function(eventData) {
      queueEvent(createCustomEvent(eventData), eventData.userFeatures, getDecisionsFromLayerStates(eventData.layerStates));
    };
  }
];

/**
 * Getter returns an  which logs a ClickEventTicket to the store
 * @return {onClickEvent}
 */
var onClickEvent = [
  /**
   * @return {onClickEvent}
   */
  function() {
    /**
     * @param {ClickEventData} eventData
     */
    return function(eventData) {
      queueEvent(createClickEvent(eventData), eventData.userFeatures, getDecisionsFromLayerStates(eventData.layerStates));
    };
  }
];

/**
 * Get eventFeatures and eventMetrics from event tags
 * @param {Object} eventTags
 * @returns {Object}
 *        A map of metric to validated metric value, plus a property 'tags' that is
 *        itself a map from event tag to value (both features and some
 *        [short-term: all] metrics).
 * @private
 */
function formatTagsAndMetrics(eventTags:any): any {
  var metricNames = _.keys(METRIC_FIELDS);

  var featureTags = _.omit(eventTags, metricNames);
  var metricTags = _.pick(eventTags, metricNames);

  function featureReducer(acc, value, key) {
    try {
      validateFeatureValue(value);
      acc[key] = value;
    } catch(e) {
      ApiEvents.emitError(new TrackerError('Bad value for eventTags[' + key + ']: ' + e.message));
    }

    return acc;
  }

  var validTags = _.reduce(featureTags, featureReducer, {});

  var validMetrics = _.reduce(metricTags, function(acc, value, key) {
    var metricDefinition = METRIC_FIELDS[key];

    // Write metrics back to features, to ensure backwards-compatibility
    // when we make new metric kinds.
    if (!metricDefinition.excludeFeature) {
      featureReducer(validTags, value, key);
    }

    try {
      metricDefinition.validate(value);

      acc[key] = metricDefinition.sanitize(value);
      // Send in tags also (effectively making the conditional !excludeFeature block above unconditional).
      // Short-term.
      validTags[key] = acc[key];
    } catch(e) {
      ApiEvents.emitError(new TrackerError('Bad value for eventMetrics[' + key + ']: ' + e.message));
    }

    return acc;
  }, {});

  validMetrics.tags = validTags;

  return validMetrics;
}

/**
 * Formats the event properties by limiting the property count to 15.
 * @param {Object} properties
 * @returns {Object}
 * @private
 */
function formatEventProperties(properties: object): object {
  const propertyCount = _.keys(properties).length;
  const limit = 15;

  if(propertyCount <= limit) {
    return properties;
  }

  const newProperties = {};
  const discardedProperties = {};
  let count = 0;

  for(const key in properties) {
    if(count < limit) {
      newProperties[key] = properties[key];
    } else {
      discardedProperties[key] = properties[key];
    }
    count++;
  }

  Logger.log('Custom event has more than 15 properties. The following properties were not pushed', discardedProperties);

  return newProperties;
}

/**
 * Return an EventTicket with base properties and additional properties:
 *
 * * `eventName`: `<pageId>`
 * * `eventFeatures`: Array of FeatureAvro generated from view tags
 *
 * where `pageId` is the ID of the view in the datastore.
 *
 * @param {ViewEventData} viewEventData
 * @returns {EventTicketPayload}
 * @private
 */
function createViewEvent(viewEventData:ViewEventData): EventTicketPayload {
  var eventTicket = _.extend({
    entity_id: viewEventData.pageId,
    key: viewEventData.pageApiName,
    timestamp: viewEventData.timestamp,
    uuid: viewEventData.eventId,
    type: VIEW_ACTIVATED_TYPE,
  }, formatTagsAndMetrics(viewEventData.eventTags));

  return eventTicket;
}

/**
 * @param {ClickEventData} clickEventData
 * @returns {EventTicketPayload}
 * @private
 */
function createClickEvent(clickEventData:ClickEventData): EventTicketPayload {
  return _.extend({
    entity_id: clickEventData.eventEntityId,
    key: clickEventData.eventApiName,
    timestamp: clickEventData.timestamp,
    uuid: clickEventData.eventId,
    type: clickEventData.eventCategory,
  }, formatTagsAndMetrics(clickEventData.eventTags));
}

/**
 * Return an EventTicket with base properties and additional properties:
 *
 * * `eventType`: `<eventCategory>`
 * * `eventName`: `<eventApiName>`
 * * `eventFeatures`: Array of FeatureAvro generated from user-supplied event tags
 *
 * where `eventName`, `category` are defined by the user in Optimizely.
 *
 * @param {CustomEventData} customEventData
 * @returns {EventTicketPayload}
 * @private
 */
function createCustomEvent(customEventData:CustomEventData): EventTicketPayload {
  return _.extend({
    entity_id: customEventData.eventEntityId,
    key: customEventData.eventApiName,
    timestamp: customEventData.timestamp,
    uuid: customEventData.eventId,
    type: customEventData.eventCategory,
    properties: formatEventProperties(customEventData.eventProperties)
  }, formatTagsAndMetrics(customEventData.eventTags));
}

/**
 * Create an EventTicket for the ClientActivation event
 * @param {ClientActivationEventData} clientActivationEventData
 * @returns {EventTicketPayload}
 * @private
 */
function createClientActivationEvent(eventData: ClientActivationEventData): /* EventTicketPayload*/ any {
  return {
    entity_id: null,
    type: CLIENT_ACTIVATION_EVENT_NAME,
    uuid: eventData.eventId,
    timestamp: eventData.timestamp,
  };
}

/**
 * Check value for validity against FeatureAvro schema (lighter-weight version for prod).
 * Throws if not valid.
 *
 * @param {*} value
 * @private
 */
function validateFeatureValue(value:any) {
  // null (or undefined) value are not allowed
  if (value == null) { // eslint-disable-line eqeqeq
    throw new Error('Feature value is null');
  }
  if (typeof value === 'object') {
    var valueStr;
    try {
      valueStr = JSONEnv.stringify(value);
    } catch(e) {} // eslint-disable-line
    throw new Error('Feature value is complex: "' + valueStr || '[object]' + '"');
  }
}

/**
 * @throws {Error} if the value is not a number
 * @private
 */
function validateNumericMetric(value) {
  // null (or undefined) value are not allowed.
  if (value == null) { // eslint-disable-line eqeqeq
    throw new Error('Metric value is null');
  }
  if (!_.isNumber(value)) {
    throw new Error('Metric value is not numeric');
  }
}

/**
 * @param {FeatureValue[]} featureValues
 * @return {VisitorAttributePayload[]}
 * @private
 */
function formatUserFeatures(featureValues:FeatureValue[]): VisitorAttributePayload[] {
  return _.reduce(featureValues, function(memo, featureValue) {
    try {
      if (__INVARIANT__) {
        require('core/utils/invariant').isType('FeatureValue', featureValue);
      }
      validateFeatureValue(featureValue.value);
      memo.push({
        entity_id: featureValue.id || null,
        key: featureValue.name,
        type: featureValue.type,
        value: featureValue.value,
      });
    } catch(e) {
      Logger.warn('Error evaluating user feature', featureValue, e);
    }
    return memo;
  }, []);
}

/**
 * Queue event to be sent
 * @private
 */
function queueEvent(eventTicket, userFeatures, decisions) {
  Flux.dispatch(actionTypes.REGISTER_TRACKER_EVENT, {
    event: eventTicket,
    decisions: decisions,
  });
  //Update visitor attributes
  updateAttributes(userFeatures);

  // Check and send for every event
  batchOrSendEvents();
}

/**
 * Update attributes
 * @private
 */
function updateAttributes(userFeatures) {
 //TODO: (Perf) Format and update user features only if changed
 var attributes = formatUserFeatures(userFeatures);
 Flux.dispatch(actionTypes.UPDATE_TRACKER_VISITOR_ATTRIBUTES, {
   attributes: attributes
 });
}

/**
 * Construct batch from decision data
 *
 * @param {DecisionData} decisionData
 * @private
 */
function createBatchFromDecisionData(decisionData:DecisionData) {
  var visitorAttr = formatUserFeatures(decisionData.userFeatures);

  var batch = {
    account_id: decisionData.accountId,
    anonymize_ip: decisionData.anonymizeIP,
    client_name: decisionData.clientName,
    client_version: decisionData.clientVersion,
    project_id: decisionData.projectId,
    visitors: [{
      session_id: formatSessionId(decisionData.sessionId),
      visitor_id: decisionData.visitorId,
      attributes: visitorAttr,
      snapshots: [{
        // Add decision/decisionEvent from decisionData
        decisions: [
          {
            campaign_id: decisionData.layerId,
            experiment_id: decisionData.experimentId,
            variation_id: decisionData.variationId,
            is_campaign_holdback: decisionData.isLayerHoldback
          }
        ],
        events: [
          {
            uuid: decisionData.decisionId,
            entity_id: decisionData.layerId,
            timestamp: decisionData.timestamp,
            type: CAMPAIGN_ACTIVATED_EVENT,
          }
        ],
      }],
    }],
  };

  Flux.dispatch(actionTypes.REGISTER_PREVIOUS_BATCH, batch);

  batchOrSendEvents();
}

/**
 * Update the batch and visitor data in the store
 * @param {ClientActivationEventData} eventData
 * @private
 */
function createBatch(eventData) {
  var anonymizeIP = !_.isNull(GlobalStore.getAnonymizeIP()) ? GlobalStore.getAnonymizeIP() : undefined;

  var ticket:any = {
    account_id: eventData.accountId,
    anonymize_ip: anonymizeIP,
    client_name: eventData.clientName,
    client_version: eventData.clientVersion,
    project_id: eventData.projectId,
    visitors: [],
  };

  if (__INCLUDE_REVISION__) {
    ticket.revision = eventData.revision;
  }

  if (ENABLE_EET) {
    ticket.enrich_decisions = true;
  }

  var visitor = {
    session_id: formatSessionId(eventData.sessionId),
    visitor_id: eventData.visitorId,
    attributes: [], //Will be updated as part of events
    snapshots: [],
  };
  // Only decisions from previous activation should be available at this time
  var persistedDecisions = getDecisionsFromLayerStates(eventData.layerStates);

  Flux.dispatch(actionTypes.REGISTER_TRACKER_VISITOR, {
    data: ticket,
    visitor: visitor,
    decisions: persistedDecisions
  });

  // Send any events that might have been waiting for the first batch.
  batchOrSendEvents();
}

function formatSessionId(sessionId) {
  if (USE_AUTO_SESSION_ID) {
    return AUTO_SESSION_ID;
  }
  return sessionId;
}

/**
 * Queue the decision and the decision event to be sent
 * @param {DecisionData} decisionData
 * @private
 */
function queueDecision(decisionData:DecisionData) {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('onLayerDecisionOptimizely');
  // Create decision event
  var decisionEvent = {
    entity_id: decisionData.layerId,
    type: CAMPAIGN_ACTIVATED_EVENT,
    uuid: decisionData.decisionId,
    timestamp: decisionData.timestamp,
  };

  //register decision
  Flux.dispatch(actionTypes.REGISTER_TRACKER_DECISION, {
    decisionEvent: decisionEvent,
    decisions: getDecisionsFromLayerStates(decisionData.layerStates),
  });

  // Update the visitor attributes
  updateAttributes(decisionData.userFeatures);
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('onLayerDecisionOptimizely');

  // Check and send for every event
  batchOrSendEvents();
}

/**
 * Check if we can send because it is set for manual sending
 * Or if there are no events to send
 * @private
 */
function maybeSendEvents() {
  // Get data from store
  if (!TrackerEventsStore.canSend()) {
    Logger.debug('Not sending events (holding)');
    return;
  }
  var hasCurrentBatch = TrackerEventsStore.hasEventsToSend();
  var hasPreviousBatches = TrackerEventsStore.hasPreviousBatchesToSend();
  if (!hasCurrentBatch && !hasPreviousBatches) {
    Logger.debug('Not sending events because there are no events to send');
    return;
  }
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('sendEventsTrackerOpti');

  if (hasPreviousBatches) {
    _.each(TrackerEventsStore.getPreviousBatches(), sendEvents);
    Flux.dispatch(actionTypes.RESET_TRACKER_PREVIOUS_BATCHES);
  }

  if (hasCurrentBatch) {
    //Creates a new snapshot of existing decision events if necessary
    Flux.dispatch(actionTypes.FINALIZE_BATCH_SNAPSHOT);
    sendEvents(TrackerEventsStore.getEventBatch());
    // clear flux events
    Flux.dispatch(actionTypes.RESET_TRACKER_EVENTS);
  }

  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('sendEventsTrackerOpti');
}

/**
 * Send the batched events
 * @private
 */
function sendEvents(baseTicket) {
  Logger.debug('Sending ticket:', baseTicket);
  if (__INVARIANT__) {
    require('core/utils/invariant').isType('EventBatch', baseTicket);
  }

  var randomId = uuid.generate();
  let options = {
    url: EVENT_ENDPOINT,
    method: 'POST',
    data: formatBatch(baseTicket),
  };
  // TODO: Default to useBeacon once redirect experiment SRM is fully mitigated and confirmed from customers
  if (TrackerEventsStore.shouldUseBeacon()) {
    xhr.sendBeacon(options, randomId);
  }
  else {
    xhr.retryableRequest(options, randomId);
  }
}

/**
 *
 * @param {EventBatch} batch
 * @return {EventBatchPayload}
 * @private
 */
function formatBatch(batch:EventBatch): EventBatchPayload {
  var formatted = _.extend({}, _.pick(batch, [
    'account_id',
    'anonymize_ip',
    'client_name',
    'client_version',
    'enrich_decisions',
    'project_id',
    'revision',
  ]), {
    visitors: _.map(batch.visitors, formatVisitor),
  });

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('EventBatchPayload', formatted);
  }

  return formatted;
}

/**
 * @param {BatchVisitor} visitor
 * @return {VisitorPayload}
 * @private
 */
function formatVisitor(visitor:BatchVisitor): VisitorPayload {
  return {
    visitor_id: visitor.visitor_id,
    session_id: AUTO_SESSION_ID,
    attributes: _.map(visitor.attributes, formatAttribute),
    snapshots: _.map(visitor.snapshots, formatSnapshot),
  };
}

/**
 *
 * @param {BatchVisitorAttribute} attribute
 * @return {VisitorAttributePayload}
 * @private
 */
function formatAttribute(attribute:BatchVisitorAttribute): VisitorAttributePayload {
  return pickWithRename(attribute, {
    entity_id: 'e',
    key: 'k',
    type: 't',
    value: 'v',
  });
}

/**
 *
 * @param {BatchEventSnapshot} snapshot
 * @return {EventSnapshotPayload}
 * @private
 */
function formatSnapshot(snapshot:BatchEventSnapshot): EventSnapshotPayload {
  var events:any = snapshot.events;
  if (__CONSOLIDATE_VIEW_EVENTS__) {
    events = consolidateEvents(events as any);
  }
  return {
    activationTimestamp: GlobalStore.getActivationTimestamp(),
    decisions: _.map(snapshot.decisions, formatDecision),
    events: _.map(events, formatEventForOpty),
  };
}

/**
 * Consolidate a list of events by combining "like" events into a single event, concatenating their
 * entity IDs and keys, delimiting them with hyphens.
 *
 * At the moment, this only groups together VIEW_ACTIVATED_TYPE events with the following properties:
 * * .tags is empty
 * * .value is not defined
 * * .reveneue is not defined
 * * .quantity is not defined
 *
 * @param {Event[]} events List of events (from a single snapshot) to consolidate
 * @return {Event[]} list of events, after consolidation
 * @private
 */
function consolidateEvents(events:Event[]): Event[] {
  var eventsByKey = _.reduce(events, function(res, event) {
    var eventKey;
    var shouldConsolidate =
      event.type === VIEW_ACTIVATED_TYPE &&
      _.isEmpty(event.tags) &&
      _.isEmpty(_.pick(event, _.keys(METRIC_FIELDS)));

    if (shouldConsolidate) {
      eventKey = event.type;
    } else {
      eventKey = event.uuid;
    }

    if (res[eventKey]) {
      var maxTimestamp = res[eventKey].timestamp;
      if (event.timestamp > maxTimestamp) {
        maxTimestamp = event.timestamp;
      }

      res[eventKey] = _.extend({}, res[eventKey], {
        key: res[eventKey].key + '-' + (event.key || ''),
        entity_id: res[eventKey].entity_id + '-' + event.entity_id,
        timestamp: maxTimestamp,
      });
    } else {
      res[eventKey] = event;
    }

    return res;
  }, {});

  return _.values(eventsByKey);
}

/**
 *
 * @param {BatchDecisionTicket} decision
 * @return {DecisionTicketPayload}
 * @private
 */
function formatDecision(decision: BatchDecisionTicket): DecisionTicketPayload {
  const payload = pickWithRename(decision, {
    campaign_id: 'c',
    experiment_id: 'x',
    is_campaign_holdback: 'h',
    variation_id: 'v',
  });

  payload.m = decision.metadata ? pickWithRename(decision.metadata, { cmab_uuid: 'cmab_uuid' }) : {};
  return payload;
}

/**
 *
 * @param {BatchEventTicket} event
 * @return {EventTicketPayload}
 * @private
 */
function formatEventForOpty(event:BatchEventTicket): EventTicketPayload {
  // Legacy: map key to type
  // TODO(CJS-1720): remove this
  if (event.key === CAMPAIGN_ACTIVATED_EVENT) {
    event.type = CAMPAIGN_ACTIVATED_EVENT;
    delete event.key;
  }
  return pickWithRename(event, {
    entity_id: 'e',
    key: 'k',
    quantity: 'q',
    revenue: '$',
    tags: 'a',
    properties: 'p',
    timestamp: 't',
    uuid: 'u',
    value: 'v',
    type: 'y',
  });
}

/**
 * Rename the keys in object which are present in remapNames
 * to their associated value in remapNames
 *
 * @param {Object} obj
 * @param {Object} remapNames
 * @return {Object} remapped object
 * @private
 */
function pickWithRename(obj:any, remapNames:any): any {
  return _.reduce(obj, function(res, val, key) {
    if (key in remapNames) {
      res[remapNames[key] || key] = val;
    }
    return res;
  }, {});
}

/**
 * Decide between batching or sending events immediately
 * @private
 */
function batchOrSendEvents() {
  if (!TrackerEventsStore.shouldBatch()) {
    maybeSendEvents();
    return;
  }
  // set up polling once
  if (!TrackerEventsStore.isPolling()) {
    function poll() {
      // if  __EXPERIMENTAL_TRACK_EVENTS_V1_ON_LOADED__ is set and
      // document is not loaded, don't send immediately
      var shouldSend = !delayUntilLoad || DocumentEnv.isLoaded();

      if(shouldSend) {
        maybeSendEvents();
      }

      if(TrackerEventsStore.isPolling()) {
        WindowEnv.setTimeout(poll, TRACK_EVENTS_POLLING_INTERVAL);
      }
    }

    WindowEnv.setTimeout(poll, TRACK_EVENTS_POLLING_INTERVAL);
    Flux.dispatch(actionTypes.SET_TRACKER_POLLING, true);

    // Disable batching/polling after polling timeout
    WindowEnv.setTimeout(function() {
      Flux.dispatch(actionTypes.SET_TRACKER_BATCHING, false);
      Flux.dispatch(actionTypes.SET_TRACKER_POLLING, false);
    }, TRACK_EVENTS_POLLING_TIMEOUT);
  }
}

function getDecisionsFromLayerStates(layerStates) {
  return _.map(layerStates, function(layer) {
    var cmab_uuid = cmabHelper.getCmabUUIDAndUpdate30MinTimeout(layer.layerId, layer.decision.experimentId);

    var metadata = cmab_uuid ? { cmab_uuid: cmab_uuid } : {};
    return {
      campaign_id: layer.layerId,
      experiment_id: layer.decision.experimentId,
      variation_id: layer.decision.variationId,
      is_campaign_holdback: layer.decision.isLayerHoldback,
      metadata: metadata,
    };
  });
}

function persistState() {
  var persistableTrackerState = TrackerEventsStore.getPersistableState();
  if (persistableTrackerState) {
    try {
      Logger.debug('Persisting pending batch:', persistableTrackerState);
      Visitor.persistTrackerOptimizelyData(persistableTrackerState);
      Flux.dispatch(actionTypes.SET_TRACKER_DIRTY, false);
    } catch(e) {
      // This is expected if it happens during initialization (which shouldn't happen, but...)
      Logger.debug('Failed to persist pending batch:', e);
      // TODO(tyler): log an error event?
    }
  }
}

var trackerOpty = {
  trackLayerDecision: trackLayerDecision,
  preRedirectPolicy: trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_AND_TRACK_DURING_REDIRECT,
  postRedirectPolicy: trackingPolicies.PostRedirectPolicies.TRACK_AFTER_SYNC,
  nonRedirectPolicy: trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY,
  onPageActivated: onPageActivated,
  onClientActivation: onClientActivation,
  onClickEvent: onClickEvent,
  onCustomEvent: onCustomEvent
};


module.exports = function(PluginManager) {
  PluginManager.registerAnalyticsTracker('optimizely', trackerOpty);

  EventEmitter.on({
    filter: {
      type: EventEmitterEnums.TYPES.ANALYTICS,
      name: 'sendEvents'
    },
    handler: function() {
      Flux.dispatch(actionTypes.SET_TRACKER_SEND_EVENTS, true);
      maybeSendEvents();
    },
  });

  EventEmitter.on({
    filter: {
      type: EventEmitterEnums.TYPES.ANALYTICS,
      name: 'maybeSendEvents'
    },
    handler: function() {
      maybeSendEvents();
    },
  });

  EventEmitter.on({
    filter: {
      type: EventEmitterEnums.TYPES.ANALYTICS,
      name: 'holdEvents',
    },
    handler: function() {
      Flux.dispatch(actionTypes.SET_TRACKER_SEND_EVENTS, false);
    },
  });

  Flux.dispatch(actionTypes.SET_TRACKER_SEND_EVENTS, !manualSend);

  if (__TRACK_EVENTS_PERSIST_PENDING__) {
    var unobserveToken = EventEmitter.on({
      filter: {
        type: 'lifecycle',
        name: 'activated'
      },
      handler: function() {
        TrackerEventsStore.observe(persistState);
        //Added so the observer is not set up twice on 'activated' event
        EventEmitter.off(unobserveToken);
      }
    });
  }
};

if (__TEST__) {
  TRACK_EVENTS_POLLING_INTERVAL = 20;

  module.exports.tracker = trackerOpty;
  module.exports.setTestOnLoad = function(test) {
    delayUntilLoad = test;
  };

  module.exports.getTestOnLoad = function() {
    return delayUntilLoad;
  };

  module.exports.setManualTest = function(test) {
    manualSend = test;
  };

  module.exports.getManualTest = function() {
    return manualSend;
  };

  module.exports.setUseAutoSessionId = function(useAutoSessionId) {
    USE_AUTO_SESSION_ID = useAutoSessionId;
  };

  module.exports.getAutoSessionId = function() {
    return USE_AUTO_SESSION_ID;
  };

  module.exports.EVENT_ENDPOINT = EVENT_ENDPOINT;
}
